import React, { useState } from 'react';
import { Card, Collapse } from 'antd';
import {
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_STYLE,
  renderSectionTitle,
} from '../orderDetailLayout';
import './OrderDetailSectionCard.css';

/**
 * Collapsible section shell for the order / supplier-order detail pages.
 *
 * Owner feedback 2026-09-14: on the supplier order (ZD) detail page every
 * section is collapsed by default, so the operator sees the whole structure of
 * the order in one screen and opens only what they need. The card chrome
 * (SECTION_CARD_STYLE) and the section title (renderSectionTitle) are the same
 * as before the change — only the body became a collapse panel.
 *
 * Header actions (`extra`) stay visible and usable while the panel is closed;
 * their click never toggles the panel.
 *
 * `forceRender` mounts the panel body immediately. Use it whenever the body
 * contains antd Form fields, data fetching or any other mount side effect that
 * must happen whether or not the operator opens the section.
 */
interface OrderDetailSectionCardProps {
  /** Section title rendered with the shared detail-page section title style. */
  title: React.ReactNode;
  /** Header actions; kept clickable while the panel is collapsed. */
  extra?: React.ReactNode;
  /** Open on mount. Default: closed (owner decision 2026-09-14). */
  defaultOpen?: boolean;
  /** Mount the body immediately (Form.Items, fetching, other mount effects). */
  forceRender?: boolean;
  /** Extra padding/overrides for the panel body, merged over the shared body style. */
  bodyStyle?: React.CSSProperties;
  style?: React.CSSProperties;
  'data-testid'?: string;
  children: React.ReactNode;
}

const PANEL_KEY = 'section';

const OrderDetailSectionCard: React.FC<OrderDetailSectionCardProps> = ({
  title,
  extra,
  defaultOpen = false,
  forceRender = false,
  bodyStyle,
  style,
  'data-testid': testId,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Card
      className="order-detail-section"
      style={{ ...SECTION_CARD_STYLE, ...style }}
      styles={{ body: { padding: 0 } }}
      data-testid={testId}
      data-open={open ? 'true' : 'false'}
    >
      <Collapse
        ghost
        activeKey={open ? [PANEL_KEY] : []}
        onChange={(keys) => setOpen((Array.isArray(keys) ? keys : [keys]).includes(PANEL_KEY))}
        items={[
          {
            key: PANEL_KEY,
            forceRender,
            label:
              typeof title === 'string' ? renderSectionTitle(title) : title,
            extra: extra ? (
              // The antd collapse header toggles on click; header actions must not.
              <span
                className="order-detail-section__actions"
                role="presentation"
                onClick={(event) => event.stopPropagation()}
                onKeyDown={(event) => event.stopPropagation()}
              >
                {extra}
              </span>
            ) : undefined,
            children: (
              <div
                className="order-detail-section__body"
                style={{ ...SECTION_CARD_BODY_STYLE, ...bodyStyle }}
              >
                {children}
              </div>
            ),
          },
        ]}
      />
    </Card>
  );
};

export default OrderDetailSectionCard;
